/**
 * Messaging Queue — live view of the Send Gate.
 * Two tabs:
 *   1. Queue — every scheduled send with template ID, contact, release time.
 *      Click ⌫ to cancel a pending item.
 *   2. Audit — every decision (allow/queue/block) with reason + segment count.
 *
 * Both auto-refresh every 30 seconds.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Trash2, RefreshCw, Filter } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

/** Turn 2026-09-05T22:30:00+00:00 into "Fri Sep 5, 6:30 PM ET". */
function fmtLocal(isoUtc) {
  if (!isoUtc) return "";
  try {
    const d = new Date(isoUtc);
    return d.toLocaleString("en-US", {
      timeZone: "America/New_York",
      weekday: "short", month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit",
    }) + " ET";
  } catch { return isoUtc; }
}

const REASON_STYLES = {
  ok: "bg-emerald-100 text-emerald-800",
  force_override: "bg-emerald-100 text-emerald-800",
  outside_window: "bg-amber-100 text-amber-800",
  reply_pause: "bg-blue-100 text-blue-800",
  rate_limit: "bg-amber-100 text-amber-800",
  workflow_spacing: "bg-amber-100 text-amber-800",
  email_daily_cap: "bg-amber-100 text-amber-800",
  kill_stage: "bg-rose-100 text-rose-800",
  kill_switch: "bg-rose-100 text-rose-800",
  no_consent: "bg-rose-100 text-rose-800",
  opted_out: "bg-rose-100 text-rose-800",
  channel_mismatch: "bg-slate-200 text-slate-700",
  no_phone: "bg-slate-200 text-slate-700",
  no_email: "bg-slate-200 text-slate-700",
};

const DECISION_STYLES = {
  allow: "bg-emerald-100 text-emerald-800 border-emerald-200",
  queue: "bg-amber-100 text-amber-800 border-amber-200",
  block: "bg-rose-100 text-rose-800 border-rose-200",
};

export default function MessagingQueue() {
  const [tab, setTab] = useState("queue"); // queue | audit
  const [queue, setQueue] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(false);
  const [channelFilter, setChannelFilter] = useState("all");
  const [decisionFilter, setDecisionFilter] = useState("all");

  async function loadQueue() {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/send-gate/queue?limit=200`, { headers: auth() });
      setQueue(r.data || []);
    } catch { toast.error("Failed to load queue"); }
    setLoading(false);
  }

  async function loadAudit() {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "200" });
      if (channelFilter !== "all") params.set("channel", channelFilter);
      if (decisionFilter !== "all") params.set("decision", decisionFilter);
      const r = await axios.get(`${API}/send-gate/audit?${params}`, { headers: auth() });
      setAudit(r.data || []);
    } catch { toast.error("Failed to load audit"); }
    setLoading(false);
  }

  useEffect(() => {
    if (tab === "queue") loadQueue(); else loadAudit();
    const t = setInterval(() => (tab === "queue" ? loadQueue() : loadAudit()), 30_000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, channelFilter, decisionFilter]);

  async function cancel(qid) {
    if (!window.confirm("Cancel this scheduled send? It will not fire.")) return;
    try {
      await axios.delete(`${API}/send-gate/queue/${qid}`, { headers: auth() });
      toast.success("Cancelled");
      loadQueue();
    } catch { toast.error("Cancel failed"); }
  }

  async function processNow() {
    try {
      const r = await axios.post(`${API}/send-gate/queue/process-now`, {}, { headers: auth() });
      toast.success(`Processed ${r.data?.processed || 0} — sent ${r.data?.sent || 0}, re-queued ${r.data?.queued || 0}`);
      loadQueue();
    } catch { toast.error("Failed"); }
  }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Messaging Queue</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Every automated send the CRM is holding, ready to fire, or has already decided on.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => (tab === "queue" ? loadQueue() : loadAudit())} data-testid="mq-refresh">
            <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
          </Button>
          {tab === "queue" && (
            <Button variant="outline" size="sm" onClick={processNow} data-testid="mq-process-now">
              Process now
            </Button>
          )}
        </div>
      </div>

      <div className="flex gap-1 border-b border-slate-200">
        <button
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === "queue" ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700"}`}
          onClick={() => setTab("queue")}
          data-testid="mq-tab-queue"
        >
          Queue ({queue.length})
        </button>
        <button
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === "audit" ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700"}`}
          onClick={() => setTab("audit")}
          data-testid="mq-tab-audit"
        >
          Audit ({audit.length})
        </button>
      </div>

      {tab === "queue" && (
        <Card className="p-0 overflow-hidden">
          {queue.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500">
              {loading ? "Loading…" : "Nothing scheduled right now. Every workflow send is caught up."}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left py-2 px-3 font-medium text-slate-600">Fires</th>
                    <th className="text-left py-2 px-3 font-medium text-slate-600">Template</th>
                    <th className="text-left py-2 px-3 font-medium text-slate-600">Workflow</th>
                    <th className="text-left py-2 px-3 font-medium text-slate-600">Channel</th>
                    <th className="text-left py-2 px-3 font-medium text-slate-600">Contact</th>
                    <th className="text-right py-2 px-3 font-medium text-slate-600">Cancel</th>
                  </tr>
                </thead>
                <tbody>
                  {queue.map(q => (
                    <tr key={q.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-2 px-3 whitespace-nowrap font-mono text-xs text-slate-700">{fmtLocal(q.release_at_utc)}</td>
                      <td className="py-2 px-3 font-medium">{q.template_id || "—"}</td>
                      <td className="py-2 px-3 text-slate-600 text-xs">{q.workflow || "—"}</td>
                      <td className="py-2 px-3"><Badge variant="outline" className="text-xs">{q.channel}</Badge></td>
                      <td className="py-2 px-3 text-xs text-slate-500">{(q.contact_id || "").slice(0, 8)}</td>
                      <td className="py-2 px-3 text-right">
                        <button className="text-slate-400 hover:text-rose-600 p-1" onClick={() => cancel(q.id)} data-testid={`mq-cancel-${q.id}`} title="Cancel">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {tab === "audit" && (
        <>
          <div className="flex items-center gap-2 text-xs">
            <Filter className="h-4 w-4 text-slate-400" />
            <select className="border border-slate-200 rounded px-2 py-1" value={channelFilter} onChange={e => setChannelFilter(e.target.value)} data-testid="mq-audit-channel">
              <option value="all">All channels</option>
              <option value="sms">SMS</option>
              <option value="email">Email</option>
            </select>
            <select className="border border-slate-200 rounded px-2 py-1" value={decisionFilter} onChange={e => setDecisionFilter(e.target.value)} data-testid="mq-audit-decision">
              <option value="all">All decisions</option>
              <option value="allow">Allowed</option>
              <option value="queue">Queued</option>
              <option value="block">Blocked</option>
            </select>
          </div>
          <Card className="p-0 overflow-hidden">
            {audit.length === 0 ? (
              <div className="p-8 text-center text-sm text-slate-500">No matching audit rows.</div>
            ) : (
              <div className="overflow-x-auto max-h-[calc(100vh-260px)] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
                    <tr>
                      <th className="text-left py-2 px-3 font-medium text-slate-600">When</th>
                      <th className="text-left py-2 px-3 font-medium text-slate-600">Decision</th>
                      <th className="text-left py-2 px-3 font-medium text-slate-600">Reason</th>
                      <th className="text-left py-2 px-3 font-medium text-slate-600">Ch</th>
                      <th className="text-left py-2 px-3 font-medium text-slate-600">Purpose</th>
                      <th className="text-left py-2 px-3 font-medium text-slate-600">Contact</th>
                      <th className="text-right py-2 px-3 font-medium text-slate-600">Seg</th>
                    </tr>
                  </thead>
                  <tbody>
                    {audit.map(a => (
                      <tr key={a.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-2 px-3 whitespace-nowrap font-mono text-xs">{fmtLocal(a.created_at)}</td>
                        <td className="py-2 px-3">
                          <span className={`text-xs px-2 py-0.5 rounded border ${DECISION_STYLES[a.decision] || ""}`}>
                            {a.decision}
                          </span>
                        </td>
                        <td className="py-2 px-3">
                          <span className={`text-xs px-2 py-0.5 rounded ${REASON_STYLES[a.reason] || "bg-slate-100 text-slate-700"}`}>
                            {a.reason}
                          </span>
                        </td>
                        <td className="py-2 px-3 text-xs">{a.channel}</td>
                        <td className="py-2 px-3 text-xs text-slate-600 font-mono truncate max-w-xs" title={a.purpose}>{a.purpose}</td>
                        <td className="py-2 px-3 text-xs text-slate-500">{a.contact_name || (a.contact_id || "").slice(0, 8)}</td>
                        <td className="py-2 px-3 text-right text-xs">{a.segments ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
